/// <reference types="vite/client" />
import * as Sentry from "@sentry/browser"
import geocoderStyles from "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css?url"
import type { User } from "@supabase/supabase-js"
import {
  createRootRouteWithContext,
  type ErrorComponentProps,
  HeadContent,
  Outlet,
  Scripts,
  useRouterState,
} from "@tanstack/react-router"
import mapboxStyles from "mapbox-gl/dist/mapbox-gl.css?url"
import type { ReactNode } from "react"

import { BodyWrapper } from "@/components/body-wrapper"
import { Toaster } from "@/components/ui/sonner"

import globalStyles from "../globals.css?url"

const TITLE = "Find A Trusted Solar Installer"
const DESCRIPTION = "We analyzed 20,000+ companies so you know who to trust."

export type UserType = "admin" | "company" | "terminal" | "consumer" | null

// The caller as the session JWT's verified claims describe it.
export type AuthUser = Pick<
  User,
  "id" | "email" | "app_metadata" | "user_metadata"
>

export type AuthContext = {
  user: AuthUser | null
  userType: UserType
}

export const Route = createRootRouteWithContext<AuthContext>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "/og-images/og-reports.png" },
      { property: "og:url", content: "https://www.wattbot.com/" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@wattbot_app" },
    ],
    links: [
      { rel: "stylesheet", href: globalStyles },
      { rel: "stylesheet", href: mapboxStyles },
      { rel: "stylesheet", href: geocoderStyles },
      { rel: "apple-touch-icon", href: "/logo.png" },
      // Google Fonts: preconnect + stylesheet
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Source+Serif+4:ital,opsz,wght@0,8..60,200..900;1,8..60,200..900&family=Lexend:wght@100..900&family=JetBrains+Mono:wght@100..800&family=Inter:opsz,wght@14..32,100..900&display=swap",
      },
    ],
  }),
  component: RootLayout,
  errorComponent: RootErrorComponent,
})

function RootErrorComponent({ error }: ErrorComponentProps) {
  Sentry.captureException(error)
  return (
    <html lang="en">
      <body>
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <h1>Something went wrong</h1>
          <p>We've been notified and are looking into it.</p>
          <button onClick={() => window.location.reload()}>Reload page</button>
        </div>
      </body>
    </html>
  )
}

function RootLayout() {
  return (
    <RootDocument>
      <Outlet />
    </RootDocument>
  )
}

function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
  const isTerminalRoute = useRouterState({
    select: ({ location }) =>
      location.pathname === "/terminal" ||
      location.pathname.startsWith("/terminal/"),
  })
  return (
    <html
      lang="en"
      style={
        {
          "--font-serif": "'Source Serif 4'",
          "--font-display": "'Lexend'",
          "--font-mono": "'JetBrains Mono'",
        } as React.CSSProperties
      }
    >
      <head>
        <HeadContent />
        <link
          rel="icon"
          type={isTerminalRoute ? "image/svg+xml" : "image/png"}
          href={
            isTerminalRoute ? "/assets/night-shift/favicon.svg" : "/logo.png"
          }
        />
      </head>
      <body>
        <BodyWrapper>
          {children}
          <Toaster />
        </BodyWrapper>
        <Scripts />
      </body>
    </html>
  )
}
